// ─────────────────────────────────────────────────────────────────────────────
// Database Repository - TypeScript Implementation
// IndexedDB database schema and initialization for Sedation App
// ─────────────────────────────────────────────────────────────────────────────

import { openDB, IDBPDatabase } from "idb";
import { DatabaseStore, DatabaseConfig } from "../../types";

// Type definition for our database instance
export type SedationAppDB = IDBPDatabase;

// Database constants
export const DB_NAME = "sedation_app";
export const DB_VERSION = 8; // v8: fix outbox indexes (byEnqueuedAt was never created)

// Database configuration
export const DATABASE_CONFIG: DatabaseConfig = {
  name: DB_NAME,
  version: DB_VERSION,
  stores: [
    {
      name: DatabaseStore.PATIENTS,
      keyPath: "Id",
      indexes: [
        { name: "byLastName", keyPath: "LastName" },
        { name: "byFirstName", keyPath: "FirstName" },
        { name: "byEmail", keyPath: "Email" },
        { name: "byCreatedDateTime", keyPath: "CreatedDateTime" },
        { name: "bySynced", keyPath: "Synced" },
        { name: "byDeactivated", keyPath: "Deactivated" },
        {
          name: "byActiveAndCreated",
          keyPath: ["Deactivated", "CreatedDateTime"],
        },
      ],
    },
    {
      name: DatabaseStore.SEDATION_DETAILS,
      keyPath: "Id",
      indexes: [
        { name: "byPatientId", keyPath: "PatientId" },
        { name: "byStatus", keyPath: "Status" },
        { name: "bySynced", keyPath: "Synced" },
        { name: "byDeactivated", keyPath: "Deactivated" },
      ],
    },
    {
      name: DatabaseStore.SEDATION_REPORTS,
      keyPath: "Id",
      indexes: [
        { name: "bySedationDetailId", keyPath: "SedationDetailId" },
        { name: "bySignedBy", keyPath: "SignedBy" },
        { name: "byCreatedDateTime", keyPath: "CreatedDateTime" },
        { name: "bySynced", keyPath: "Synced" },
        { name: "byDeactivated", keyPath: "Deactivated" },
      ],
    },
    {
      name: DatabaseStore.MEASUREMENTS,
      keyPath: "Id",
      indexes: [
        { name: "bySedationDetailId", keyPath: "SedationDetailId" },
        { name: "byCreatedDateTime", keyPath: "CreatedDateTime" },
        { name: "bySynced", keyPath: "Synced" },
        { name: "byDeactivated", keyPath: "Deactivated" },
      ],
    },
    {
      name: DatabaseStore.SEDATION_IMAGES,
      keyPath: "Id",
      indexes: [
        { name: "byPatientId", keyPath: "PatientId" },
        { name: "bySedationId", keyPath: "SedationId" },
        { name: "byHourNumber", keyPath: "HourNumber" },
        { name: "byPatientSedation", keyPath: ["PatientId", "SedationId"] },
        { name: "byCreatedDateTime", keyPath: "CreatedDateTime" },
        { name: "bySynced", keyPath: "Synced" },
        { name: "byDeactivated", keyPath: "Deactivated" },
      ],
    },
    {
      name: DatabaseStore.OUTBOX,
      keyPath: "id",
      indexes: [
        { name: "byEnqueuedAt", keyPath: "enqueuedAt" },
        { name: "byAttempts", keyPath: "attempts" },
      ],
    },
    {
      name: DatabaseStore.META,
      keyPath: "key",
      indexes: [],
    },
    {
      name: DatabaseStore.BACKUP_METADATA,
      keyPath: "Id",
      indexes: [
        { name: "PatientId", keyPath: "PatientId" },
        { name: "SedationId", keyPath: "SedationId" },
        { name: "SyncStatus", keyPath: "SyncStatus" },
        { name: "BackupType", keyPath: "BackupType" },
        { name: "GeneratedAt", keyPath: "GeneratedAt" },
        { name: "patient_sedation", keyPath: ["PatientId", "SedationId"] },
      ],
    },
    {
      name: DatabaseStore.CPD_FLAGS,
      keyPath: "Id",
      indexes: [
        { name: "SedationDetailId", keyPath: "SedationDetailId" },
        { name: "PatientId", keyPath: "PatientId" },
        { name: "CreatedDateTime", keyPath: "CreatedDateTime" },
        { name: "Synced", keyPath: "Synced" },
      ],
    }
  ],
};

// Store setup functions
const setupPatientIndexes = (store: any): void => {
  // Basic indexes for search and filtering
  store.createIndex("byLastName", "LastName");
  store.createIndex("byFirstName", "FirstName");
  store.createIndex("byEmail", "Email");
  store.createIndex("byCreatedDateTime", "CreatedDateTime");

  // Status indexes for sync and active record management
  store.createIndex("bySynced", "Synced");
  store.createIndex("byDeactivated", "Deactivated");

  // Compound index for efficient querying of active records by creation date
  store.createIndex("byActiveAndCreated", ["Deactivated", "CreatedDateTime"]);
};

const setupSedationDetailIndexes = (store: any): void => {
  store.createIndex("byPatientId", "PatientId");
  store.createIndex("byStatus", "Status");
  store.createIndex("bySynced", "Synced");
  store.createIndex("byDeactivated", "Deactivated");
};

const setupSedationReportIndexes = (store: any): void => {
  store.createIndex("bySedationDetailId", "SedationDetailId");
  store.createIndex("bySignedBy", "SignedBy");
  store.createIndex("byCreatedDateTime", "CreatedDateTime");
  store.createIndex("bySynced", "Synced");
  store.createIndex("byDeactivated", "Deactivated");
};

const setupMeasurementIndexes = (store: any): void => {
  store.createIndex("bySedationDetailId", "SedationDetailId");
  store.createIndex("byCreatedDateTime", "CreatedDateTime");
  store.createIndex("bySynced", "Synced");
  store.createIndex("byDeactivated", "Deactivated");
};

const setupSedationImageIndexes = (store: any): void => {
  store.createIndex("byPatientId", "PatientId");
  store.createIndex("bySedationId", "SedationId");
  store.createIndex("byHourNumber", "HourNumber");
  store.createIndex("byPatientSedation", ["PatientId", "SedationId"]);
  store.createIndex("byCreatedDateTime", "CreatedDateTime");
  store.createIndex("bySynced", "Synced");
  store.createIndex("byDeactivated", "Deactivated");
};

const setupSedationSessionsIndexes = (store: any): void => {
  store.createIndex("byPatientId", "patientId");
  store.createIndex("bySedationId", "sedationId");
  store.createIndex("byCreatedAt", "createdAt");
  store.createIndex("byStatus", "sessionState.status");
  store.createIndex("byPatientSedation", ["patientId", "sedationId"]);
  store.createIndex("bySynced", "synced");
};

const setupOutboxIndexes = (store: any): void => {
  store.createIndex("byEnqueuedAt", "enqueuedAt", { unique: false });
  store.createIndex("byAttempts", "attempts", { unique: false });
};

const setupBackupMetadataIndexes = (store: any): void => {
  store.createIndex("PatientId", "PatientId", { unique: false });
  store.createIndex("SedationId", "SedationId", { unique: false });
  store.createIndex("SyncStatus", "SyncStatus", { unique: false });
  store.createIndex("BackupType", "BackupType", { unique: false });
  store.createIndex("GeneratedAt", "GeneratedAt", { unique: false });
  // Compound index for PatientId and SedationId
  store.createIndex("patient_sedation", ["PatientId", "SedationId"], { unique: false });
};

const setupCpdFlagsIndexes = (store: any): void => {
  store.createIndex("SedationDetailId", "SedationDetailId", { unique: false });
  store.createIndex("PatientId", "PatientId", { unique: false });
  store.createIndex("CreatedDateTime", "CreatedDateTime", { unique: false });
  store.createIndex("Synced", "Synced", { unique: false });
};

// Database initialization and connection

// Database initialization and connection
export async function getDb(): Promise<SedationAppDB> {
  try {
    return await openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion, transaction) {

        // Patients store
        if (!db.objectStoreNames.contains(DatabaseStore.PATIENTS)) {
          const patientsStore = db.createObjectStore(DatabaseStore.PATIENTS, {
            keyPath: "Id",
          });
          setupPatientIndexes(patientsStore);
        }

        // Sedation Details store
        if (!db.objectStoreNames.contains(DatabaseStore.SEDATION_DETAILS)) {
          const sedationDetailsStore = db.createObjectStore(
            DatabaseStore.SEDATION_DETAILS,
            { keyPath: "Id" }
          );
          setupSedationDetailIndexes(sedationDetailsStore);
        }

        // Sedation Reports store
        if (!db.objectStoreNames.contains(DatabaseStore.SEDATION_REPORTS)) {
          const sedationReportsStore = db.createObjectStore(
            DatabaseStore.SEDATION_REPORTS,
            { keyPath: "Id" }
          );
          setupSedationReportIndexes(sedationReportsStore);
        }

        // Measurements store
        if (!db.objectStoreNames.contains(DatabaseStore.MEASUREMENTS)) {
          const measurementsStore = db.createObjectStore(
            DatabaseStore.MEASUREMENTS,
            { keyPath: "Id" }
          );
          setupMeasurementIndexes(measurementsStore);
        }

        // Outbox store for sync operations
        if (!db.objectStoreNames.contains(DatabaseStore.OUTBOX)) {
          const outboxStore = db.createObjectStore(DatabaseStore.OUTBOX, {
            keyPath: "id",
          });
          setupOutboxIndexes(outboxStore);
        }

        if (!db.objectStoreNames.contains(DatabaseStore.SEDATION_SESSIONS)) {
          const store = db.createObjectStore(DatabaseStore.SEDATION_SESSIONS, {
            keyPath: "sessionId",
          });
          setupSedationSessionsIndexes(store);
        }

        // Sedation Images store for captured monitoring images
        if (!db.objectStoreNames.contains(DatabaseStore.SEDATION_IMAGES)) {
          const sedationImagesStore = db.createObjectStore(
            DatabaseStore.SEDATION_IMAGES,
            { keyPath: "Id" }
          );
          setupSedationImageIndexes(sedationImagesStore);
        }

        // Meta store for key-value data
        if (!db.objectStoreNames.contains(DatabaseStore.META)) {
          db.createObjectStore(DatabaseStore.META, { keyPath: "key" });
        }

        // Backup Metadata store for tracking sync and backup information
        if (!db.objectStoreNames.contains(DatabaseStore.BACKUP_METADATA)) {
          const backupMetadataStore = db.createObjectStore(DatabaseStore.BACKUP_METADATA, { 
            keyPath: "Id" 
          });
          setupBackupMetadataIndexes(backupMetadataStore);
        }

        // CPD Flags store for case highlights
        if (!db.objectStoreNames.contains(DatabaseStore.CPD_FLAGS)) {
          const cpdFlagsStore = db.createObjectStore(DatabaseStore.CPD_FLAGS, {
            keyPath: "Id"
          });
          setupCpdFlagsIndexes(cpdFlagsStore);
        }

        // v8 migration: add missing outbox indexes on existing databases
        // Previous versions were missing byEnqueuedAt which peekOutbox requires.
        if (oldVersion < 8 && db.objectStoreNames.contains(DatabaseStore.OUTBOX)) {
          const outboxStore = transaction.objectStore(DatabaseStore.OUTBOX);
          if (!outboxStore.indexNames.contains('byEnqueuedAt')) {
            outboxStore.createIndex('byEnqueuedAt', 'enqueuedAt', { unique: false });
          }
          if (!outboxStore.indexNames.contains('byAttempts')) {
            outboxStore.createIndex('byAttempts', 'attempts', { unique: false });
          }
        }
      },
      blocked() {
        console.warn("Database upgrade blocked - another tab might be open");
      },
      blocking() {
        console.warn("Database blocking - consider closing this tab");
      },
      terminated() {
        console.error("Database connection was terminated unexpectedly");
      },
    });
  } catch (error) {
    console.error("Failed to open database:", error);
    throw new Error(
      `Database initialization failed: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

// Database utility functions
export async function clearStore(storeName: DatabaseStore): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(storeName, "readwrite");
  await tx.objectStore(storeName).clear();
  await tx.done;
}

export async function getStoreCount(storeName: DatabaseStore): Promise<number> {
  const db = await getDb();
  return await db.count(storeName);
}

export async function deleteDatabase(): Promise<void> {
  try {
    // Close any open connections first
    const db = await getDb();
    db.close();

    // Delete the database
    await new Promise<void>((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
      deleteRequest.onsuccess = () => resolve();
      deleteRequest.onerror = () => reject(deleteRequest.error);
      deleteRequest.onblocked = () => {
        console.warn(
          "Database deletion blocked - close all tabs using this database"
        );
      };
    });

  } catch (error) {
    console.error("Failed to delete database:", error);
    throw error;
  }
}

// Database health check
export async function checkDatabaseHealth(): Promise<{
  isHealthy: boolean;
  version: number;
  stores: string[];
  errors: string[];
}> {
  const result = {
    isHealthy: true,
    version: 0,
    stores: [] as string[],
    errors: [] as string[],
  };

  try {
    const db = await getDb();
    result.version = db.version;
    result.stores = Array.from(db.objectStoreNames) as string[];

    // Verify expected stores exist
    const expectedStores = Object.values(DatabaseStore);
    for (const storeName of expectedStores) {
      if (!db.objectStoreNames.contains(storeName)) {
        result.errors.push(`Missing store: ${storeName}`);
        result.isHealthy = false;
      }
    }

    db.close();
  } catch (error) {
    result.isHealthy = false;
    result.errors.push(
      `Database connection failed: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }

  return result;
}

// Export types for use by other modules
export type DatabaseInstance = SedationAppDB;
